import { useReducer, useEffect, useCallback } from 'react';

import { flattenObjectData } from '../general/flattenObjectData';

const useForm = (
	initialValues = {},
	onSubmit,
	onSuccess,
	onError,
	validation,
) => {

	const [{
		form,
		errors,
		error,
		submitting,
		shouldSubmitForm,
		madeAnyChanges,
		canSubmit,
	}, dispatch] = useReducer(formReducer, getInitialState({ initialValues, validation }));

	const resetForm = useCallback(
		() => dispatch({ type: 'reset', values: getInitialState({ initialValues, validation }) }),
		[dispatch, initialValues, validation],
	);

	const updateFormValues = useCallback((fieldValueObject) => {
		const nextForm = {
			...form,
			...fieldValueObject,
		};
		const nextErrors = getUpdatedErrors({
			currentErrors: errors,
			nextForm,
			changedValues: fieldValueObject,
			validation,
		});

		return dispatch({
			type: 'setFormValues',
			values: fieldValueObject,
			errors: nextErrors,
		});
	}, [dispatch, errors, form, validation]);

	// to deprecate
	const onChange = useCallback(field => value => {
		return updateFormValues({ [field]: value });
	}, [updateFormValues]);

	const changeFormValue = useCallback(fieldValueObject => updateFormValues(fieldValueObject), [updateFormValues]);

	const setError = useCallback(id => error => dispatch({ type: 'setErrorValues', values: { errors: { [id]: error } } }), [dispatch]);

	const setErrors = useCallback(errors => {
		dispatch({ type: 'setErrorValues', values: { errors } });
	}, [dispatch]);

	const submitForm = useCallback(() => {
		dispatch({
			type: 'set',
			values: {
				shouldSubmitForm: true,
			},
		});
	}, [dispatch]);

	// this effect handles submitting form
	useEffect(() => {
		if (shouldSubmitForm && !submitting) {
			(async () => {
				if (validation) {
					const validationResult = validation(form);
					const newErrors = validationResult.filter(v => !v.rule).reduce((acc, v) => {
						acc[v.field] = v.error;
						return acc;
					}, {});
					if (Object.keys(newErrors).length) {
						dispatch({
							type: 'setErrorValues',
							values: {
								errors: newErrors,
								submitting: false,
								shouldSubmitForm: false,
							},
						});

						return;
					}
				}

				// Strip all objects to be IDs.
				// We have decided in general and in principal that forms should store objects as
				// values, as it makes controlled form components vastly more elegant & correct.
				const flattenedForm = flattenObjectData(form);

				dispatch({ type: 'setSubmitting', value: true });

				try {
					const payload = await onSubmit(flattenedForm);
					if (payload) {
						dispatch({
							type: 'set',
							values: {
								submitting: false,
								shouldSubmitForm: false,
							},
						});

						// this is to allow a parent to handle the payload if success
						if (onSuccess) {
							onSuccess(payload);
						}
					}
				}
				catch (e) {
					dispatch({
						type: 'set',
						values: {
							error,
							submitting: false,
							shouldSubmitForm: false,
						},
					});

					// this is to allow a parent to handle the payload if error
					if (onError) {
						onError(e);
					}
				}
			})();
		}
	}, [shouldSubmitForm, submitting, onSubmit, onSuccess, onError, dispatch, form, error, validation]);

	return {
		changeFormValue,
		submitForm,
		onChange,
		setError,
		setErrors,
		resetForm,
		values: form,
		error,
		errors,
		submitting: submitting || shouldSubmitForm,
		madeAnyChanges,
		canSubmit,
	};
};


const getInitialState = ({ validation, initialValues }) => {
	const canSubmit = !validation || !validation(initialValues).filter(v => !v.rule).length;

	return {
		form: initialValues,
		errors: {},
		error: null,
		submitting: false,
		shouldSubmitForm: false,
		madeAnyChanges: false,
		canSubmit,
	};
};

function formReducer (state, action) {
	switch (action.type) {
		case 'reset':
			return action.values;
		case 'setFormValues':
			return {
				...state,
				form: {
					...state.form,
					...action.values,
				},
				errors: {
					...state.errors,
					...(action.errors || {}),
				},
				error: null,
				madeAnyChanges: true,
			};
		case 'setErrorValues':
			return {
				...state,
				...action.values,
				errors: {
					...state.errors,
					...(action.values.errors || {}),
				},
			};
		case 'setSubmitting':
			return {
				...state,
				submitting: action.value,
			};
		case 'canSubmit':
			return {
				...state,
				canSubmit: action.value,
			};
		case 'set':
			return {
				...state,
				...action.values,
			};
	}
}

const getUpdatedErrors = ({
	currentErrors = {},
	nextForm,
	changedValues = {},
	validation,
}) => {
	const changedFields = Object.keys(changedValues);
	const currentErrorFields = Object.keys(currentErrors).filter(field => Boolean(currentErrors[field]));

	if (!changedFields.length && !currentErrorFields.length) {
		return {};
	}

	const updatedErrors = {};

	if (!validation) {
		changedFields.forEach(field => {
			updatedErrors[field] = null;
		});

		return updatedErrors;
	}

	const validationByField = validation(nextForm).reduce((acc, result) => {
		acc[result.field] = result;
		return acc;
	}, {});

	currentErrorFields.forEach(field => {
		const validationResult = validationByField[field];

		if (validationResult) {
			updatedErrors[field] = validationResult.rule ? null : validationResult.error;
			return;
		}

		updatedErrors[field] = null;
	});

	changedFields.forEach(field => {
		if (updatedErrors[field] === undefined) {
			updatedErrors[field] = null;
		}
	});

	return updatedErrors;
};

export default useForm;
